import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import gsap from "gsap";
import { LETTER_IMAGE } from "../config";

/* Stages: "sealed" → "open" */

export default function Letter({ copy, onSealBroken }) {
  const [stage, setStage] = useState("sealed");
  const [hasImage, setHasImage] = useState(false);
  const sealedRef = useRef(null);
  const openRef = useRef(null);
  const rootRef = useRef(null);

  // Use the real scanned letter on the sealed screen if it exists,
  // otherwise the CSS envelope.
  useEffect(() => {
    const img = new Image();
    img.onload = () => setHasImage(true);
    img.onerror = () => setHasImage(false);
    img.src = LETTER_IMAGE;
  }, []);

  const open = () => {
    if (stage !== "sealed") return;
    gsap.context(() => {
      gsap
        .timeline({ onComplete: () => setStage("open") })
        .to(sealedRef.current, { scale: 1.06, duration: 0.35, ease: "power2.out" })
        .to(sealedRef.current, {
          opacity: 0,
          scale: 1.5,
          filter: "blur(6px)",
          duration: 0.5,
          ease: "power2.in",
        });
    }, rootRef);
  };

  // The sheet unfolds, then the lines settle in.
  useEffect(() => {
    if (stage !== "open" || !openRef.current) return;
    const ctx = gsap.context(() => {
      gsap
        .timeline()
        .from(openRef.current.querySelector("[data-paper]"), {
          scaleY: 0.55,
          opacity: 0,
          transformOrigin: "center center",
          duration: 0.8,
          ease: "power3.out",
        })
        .from(
          openRef.current.querySelectorAll("[data-stagger]"),
          {
            opacity: 0,
            y: 14,
            duration: 0.8,
            stagger: 0.15,
            ease: "power2.out",
          },
          "-=0.3"
        );
    }, rootRef);
    return () => ctx.revert();
  }, [stage]);

  const breakSeal = () => {
    gsap.context(() => {
      gsap.to(openRef.current, {
        opacity: 0,
        scale: 0.97,
        duration: 0.7,
        ease: "power2.inOut",
        onComplete: onSealBroken,
      });
    }, rootRef);
  };

  return (
    <Root ref={rootRef}>
      {stage === "sealed" && (
        <Sealed
          ref={sealedRef}
          onClick={open}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && open()}
        >
          <Found>{copy.found}</Found>
          {hasImage ? (
            <LetterPhoto src={LETTER_IMAGE} alt="" />
          ) : (
            <Envelope>
              <Fold />
              <Wax />
            </Envelope>
          )}
          <Tap>{copy.tapLetter}</Tap>
        </Sealed>
      )}

      {stage === "open" && (
        <Opened ref={openRef}>
          <PaperWrap>
            <Paper data-paper>
              <Grain />
              <Foxing />
              <Creases />

              <Ink>
                {copy.letterBody.map((para, i) => (
                  <Para key={i} data-stagger $lead={i === 0}>
                    {para}
                  </Para>
                ))}

                <SealButton data-stagger onClick={breakSeal}>
                  <SealDisc />
                  <SealLabel>{copy.activateSeal}</SealLabel>
                </SealButton>
              </Ink>
            </Paper>
          </PaperWrap>
        </Opened>
      )}
    </Root>
  );
}

/* ================================================================== *
 * SEALED SCREEN
 * ================================================================== */

const Root = styled.div`
  position: fixed;
  inset: 0;
  background: var(--ink);
  display: grid;
  place-items: center;
  padding: env(safe-area-inset-top) 5vw env(safe-area-inset-bottom);
`;

const drift = keyframes`
  0%, 100% { transform: translateY(0) rotate(-0.6deg); }
  50%      { transform: translateY(-9px) rotate(0.4deg); }
`;

const Sealed = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.4rem;
  cursor: pointer;
`;

const Found = styled.h1`
  margin: 0;
  font-family: var(--font-display);
  font-weight: 400;
  font-size: clamp(2rem, 9vw, 3rem);
  color: var(--parchment);
`;

const LetterPhoto = styled.img`
  width: min(70vw, 300px);
  animation: ${drift} 6s ease-in-out infinite;
  filter: drop-shadow(0 24px 46px rgba(0, 0, 0, 0.75));
`;

const Envelope = styled.div`
  position: relative;
  width: min(70vw, 300px);
  aspect-ratio: 3 / 2;
  background: linear-gradient(160deg, #cbb992 0%, #a8946b 55%, #8d7a55 100%);
  box-shadow: 0 24px 46px rgba(0, 0, 0, 0.75),
    inset 0 0 60px rgba(80, 60, 30, 0.35);
  animation: ${drift} 6s ease-in-out infinite;
`;

const Fold = styled.div`
  position: absolute;
  inset: 0;
  clip-path: polygon(0 0, 100% 0, 50% 62%);
  background: rgba(0, 0, 0, 0.12);
  border-bottom: 1px solid rgba(60, 44, 22, 0.4);
`;

const Wax = styled.div`
  position: absolute;
  left: 50%;
  top: 58%;
  width: 21%;
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #a8412c, var(--wax) 60%, #4d180f);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.5);
`;

const Tap = styled.div`
  font-size: 0.68rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--parchment);
  opacity: 0.45;
`;

/* ================================================================== *
 * OPENED LETTER — the aged sheet
 * ================================================================== */

const Opened = styled.div`
  width: 100%;
  max-width: 33rem;
  max-height: 100dvh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 5vh 0;
`;

/* drop-shadow on the wrapper follows the clip-path of the sheet,
   which box-shadow would not */
const PaperWrap = styled.div`
  filter: drop-shadow(0 26px 50px rgba(0, 0, 0, 0.72));
  transform: rotate(-0.45deg);
`;

/* Deckled edge — irregular enough to read as torn, shallow enough
   that it never cuts into the text. */
const DECKLE = `polygon(
  0.4% 0.8%, 12% 0.2%, 26% 1.1%, 39% 0.3%, 52% 1%, 65% 0.2%,
  78% 0.9%, 91% 0.3%, 99.6% 0.9%,
  99.2% 14%, 99.8% 30%, 99.1% 46%, 99.7% 62%, 99% 78%, 99.6% 92%, 99.3% 99.2%,
  86% 99.7%, 72% 99%, 58% 99.8%, 44% 99.1%, 30% 99.7%, 16% 99%, 0.7% 99.4%,
  0.2% 85%, 0.9% 70%, 0.3% 55%, 1% 40%, 0.2% 25%, 0.8% 12%
)`;

const Paper = styled.div`
  position: relative;
  clip-path: ${DECKLE};
  padding: clamp(2.4rem, 9vw, 3.6rem) clamp(1.8rem, 7vw, 3rem);
  overflow: hidden;

  /* base sheet: warm, uneven, darker toward the edges */
  background-color: #d9c8a4;
  background-image:
    radial-gradient(80% 55% at 22% 18%, rgba(255, 248, 228, 0.75), transparent 70%),
    radial-gradient(70% 60% at 82% 88%, rgba(154, 122, 74, 0.35), transparent 72%),
    linear-gradient(168deg, #e2d3b2 0%, #d6c39c 42%, #c4ad82 100%);
`;

/* fibre + speckle, drawn once as a data-URI so there's no runtime
   filter cost on scroll */
const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.34;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='280' height='280'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='280' height='280' filter='url(%23n)'/%3E%3C/svg%3E"),
    repeating-linear-gradient(
      92deg,
      rgba(120, 92, 52, 0.05) 0 1px,
      transparent 1px 4px
    ),
    repeating-linear-gradient(
      2deg,
      rgba(120, 92, 52, 0.04) 0 1px,
      transparent 1px 6px
    );
`;

/* age spots, and the darkening that collects along a torn edge */
const Foxing = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    radial-gradient(circle at 14% 26%, rgba(126, 88, 44, 0.16), transparent 26%),
    radial-gradient(circle at 88% 12%, rgba(126, 88, 44, 0.13), transparent 20%),
    radial-gradient(circle at 74% 62%, rgba(108, 72, 36, 0.1), transparent 30%),
    radial-gradient(circle at 30% 84%, rgba(126, 88, 44, 0.14), transparent 24%),
    radial-gradient(circle at 52% 44%, rgba(140, 104, 58, 0.07), transparent 40%),
    linear-gradient(to right, rgba(96, 66, 32, 0.3), transparent 9%, transparent 91%, rgba(96, 66, 32, 0.3)),
    linear-gradient(to bottom, rgba(96, 66, 32, 0.26), transparent 7%, transparent 93%, rgba(96, 66, 32, 0.3));
`;

/* the two horizontal creases from being folded in three */
const Creases = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(
      to bottom,
      transparent calc(33.3% - 2px),
      rgba(120, 88, 48, 0.22) 33.3%,
      rgba(255, 250, 235, 0.3) calc(33.3% + 1px),
      transparent calc(33.3% + 3px)
    ),
    linear-gradient(
      to bottom,
      transparent calc(66.6% - 2px),
      rgba(120, 88, 48, 0.2) 66.6%,
      rgba(255, 250, 235, 0.28) calc(66.6% + 1px),
      transparent calc(66.6% + 3px)
    );
`;

const Ink = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Para = styled.p`
  margin: 0;
  font-family: var(--font-display);
  font-size: ${(p) =>
    p.$lead ? "clamp(1.3rem, 5.6vw, 1.6rem)" : "clamp(1.05rem, 4.3vw, 1.28rem)"};
  line-height: ${(p) => (p.$lead ? 1.44 : 1.68)};
  color: #43301b;
  /* ink bleeds very slightly into the fibre */
  text-shadow: 0 0 1px rgba(67, 48, 27, 0.28);

  ${(p) =>
    p.$lead &&
    `
    font-style: italic;
    color: #3a2916;
  `}

  &:last-of-type {
    margin-bottom: 0.4rem;
  }
`;

const SealButton = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.4rem;
  padding: 0;
  align-self: flex-start;
`;

const glowRing = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(125, 42, 30, 0.4); }
  50%      { box-shadow: 0 0 0 13px rgba(125, 42, 30, 0); }
`;

const SealDisc = styled.span`
  width: 46px;
  height: 46px;
  flex: none;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #a8412c, var(--wax) 60%, #4d180f);
  box-shadow: 0 2px 8px rgba(60, 30, 12, 0.45);
  animation: ${glowRing} 2.8s ease-out infinite;
`;

const SealLabel = styled.span`
  font-family: var(--font-body);
  font-size: 0.7rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #6b3a22;
  text-align: left;
`;
